import type { ReactNode } from 'react';
import {
    ArrowTopRightOnSquareIcon,
    FlagIcon,
    PhoneIcon,
    UsersIcon
} from '@heroicons/react/24/outline';
import { Tooltip, type DataTableColumn } from '@odo/shared-ui';
import { RowMenu, type RowMenuItem } from '../../billing/RowMenu';
import {
    missingPracticeFields,
    type PracticeListItem,
    type PracticesSortBy
} from '../../../services/accounts';
import { formatCreatedDate, formatShortDate } from '../../../utils/dates';
import { accountTypeTone } from '../accountTypeTone';
import { IdentityChip } from '../IdentityChip';
import { RiskStatusPill } from '../../shared/RiskStatusPill';
import { MissingHint } from './MissingHint';
import { OpenCallsCell } from './OpenCallsCell';
import { formatDoctorLine } from './practiceFormatting';

// This table needs 7 columns to fit, so cells use px-3 (not the billing
// tables' px-6) - same convention as patientColumns.tsx's PATIENTS_TD_PADDING.
export const PRACTICES_TD_PADDING = 'px-3 first:pl-5 last:pr-4 overflow-hidden';
export const PRACTICES_TH_PADDING = '!px-3 first:!pl-5 last:!pr-4';

// Plain functions, not components: a "*Columns.tsx" file's only component
// export is column-preset render output (see patientColumns.tsx's convention).
function practiceCell(practice: PracticeListItem): ReactNode {
    const missing = missingPracticeFields(practice);
    const doctorLine = formatDoctorLine(practice.doctorName);
    // Reveals sit on the two text spans, NOT on the column's `getTitle`: the
    // cell also hosts MissingHint, whose own Tooltip would open alongside a
    // cell-level one when hovering the chip.
    return (
        <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 min-w-0">
                <Tooltip content={practice.name}>
                    <span className="font-semibold text-gray-900 truncate min-w-0">
                        {practice.name}
                    </span>
                </Tooltip>
                {/* Chips group stays a fixed size (shrink-0) so the name -
                    not the chips - gives up space and truncates first. */}
                <div className="flex items-center gap-2 shrink-0">
                    <RiskStatusPill riskStatus={practice.riskStatus} riskNote={practice.riskNote} />
                    <MissingHint missing={missing} />
                    {practice.accountType && (
                        <IdentityChip tone={accountTypeTone(practice.accountType)}>
                            {practice.accountType}
                        </IdentityChip>
                    )}
                    {practice.version && <IdentityChip>{practice.version}</IdentityChip>}
                </div>
            </div>
            {doctorLine && (
                <Tooltip content={doctorLine}>
                    <span className="block text-[12px] text-gray-500 truncate">{doctorLine}</span>
                </Tooltip>
            )}
        </div>
    );
}

function menuItemsFor(
    practice: PracticeListItem,
    onOpenPractice: (id: string) => void
): RowMenuItem[] {
    return [
        {
            label: 'Open practice',
            icon: <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />,
            onClick: () => onOpenPractice(practice.id)
        },
        {
            label: 'Log a call',
            icon: <PhoneIcon className="h-4 w-4 text-gray-400" />,
            disabled: true,
            disabledReason: 'Coming soon - call logging is not available yet',
            onClick: () => undefined
        },
        {
            label: 'New action item',
            icon: <FlagIcon className="h-4 w-4 text-gray-400" />,
            disabled: true,
            disabledReason: 'Coming soon - action items are not available yet',
            onClick: () => undefined
        },
        {
            label: 'View patients',
            icon: <UsersIcon className="h-4 w-4 text-gray-400" />,
            disabled: true,
            disabledReason:
                "Coming soon - jumping to this practice's patients is not available yet",
            onClick: () => undefined
        }
    ];
}

export interface BuildPracticeColumnsOptions {
    onOpenPractice: (id: string) => void;
}

// Practice/Patients/Added are the only sortable columns (server `sort: 'name'
// | 'patients' | 'date'`) - Location and Last call have no server-side sort
// equivalent, so they stay plain headers. Doctor sort was dropped along with
// the Doctor column: the doctor name now lives as a secondary line under the
// practice name, so sorting by it is no longer a meaningful header action -
// the backend's `doctor` sort param is untouched for future reuse.
export function buildPracticeColumns({
    onOpenPractice
}: BuildPracticeColumnsOptions): DataTableColumn<PracticeListItem, PracticesSortBy>[] {
    return [
        {
            key: 'practice',
            label: 'Practice',
            width: '30%',
            sortable: true,
            sortKey: 'name',
            cellClassName: PRACTICES_TD_PADDING,
            headerClassName: PRACTICES_TH_PADDING,
            render: practiceCell
        },
        {
            key: 'location',
            label: 'Location',
            width: '16%',
            cellClassName: `${PRACTICES_TD_PADDING} text-sm text-gray-600 whitespace-nowrap`,
            headerClassName: `${PRACTICES_TH_PADDING} whitespace-nowrap`,
            render: (practice) =>
                practice.city && practice.state ? (
                    `${practice.city}, ${practice.state}`
                ) : (
                    <span className="text-gray-300">-</span>
                )
        },
        {
            key: 'patients',
            label: 'Patients',
            width: '10%',
            sortable: true,
            sortKey: 'patients',
            cellClassName: `${PRACTICES_TD_PADDING} tabular-nums font-medium text-gray-700 whitespace-nowrap`,
            headerClassName: `${PRACTICES_TH_PADDING} whitespace-nowrap`,
            // patientCount is always a real number - 0 is a genuine count,
            // just styled muted rather than treated as missing data.
            render: (practice) =>
                practice.patientCount > 0 ? (
                    practice.patientCount
                ) : (
                    <span className="text-gray-300">0</span>
                )
        },
        {
            key: 'openCalls',
            label: 'Open calls',
            width: '14%',
            cellClassName: PRACTICES_TD_PADDING,
            headerClassName: `${PRACTICES_TH_PADDING} whitespace-nowrap`,
            render: (practice) => <OpenCallsCell practice={practice} />
        },
        {
            key: 'lastCall',
            label: 'Last call',
            width: '12%',
            cellClassName: `${PRACTICES_TD_PADDING} text-sm text-gray-500 whitespace-nowrap`,
            headerClassName: `${PRACTICES_TH_PADDING} whitespace-nowrap`,
            render: (practice) =>
                practice.lastCallAt ? formatShortDate(practice.lastCallAt) : null
        },
        {
            key: 'added',
            label: 'Added',
            width: '12%',
            sortable: true,
            sortKey: 'date',
            cellClassName: `${PRACTICES_TD_PADDING} text-sm text-gray-500 whitespace-nowrap`,
            headerClassName: PRACTICES_TH_PADDING,
            render: (practice) => formatCreatedDate(practice.createdAt)
        },
        {
            key: 'menu',
            label: '',
            width: '6%',
            // No overflow-hidden on the menu cell (unlike PRACTICES_TD_PADDING):
            // it would clip the RowMenu dropdown, which is absolutely
            // positioned inside this cell, entirely out of view.
            cellClassName: 'px-3 last:pr-4',
            headerClassName: PRACTICES_TH_PADDING,
            stopClickPropagation: true,
            render: (practice) => (
                <div className="flex justify-end">
                    <RowMenu items={menuItemsFor(practice, onOpenPractice)} />
                </div>
            )
        }
    ];
}
